import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import {
  Button,
  EmptyState,
  FormField,
  IconButton,
  LoadingState,
  SectionCard,
  SelectInput,
  StatusBadge,
  TextAreaInput,
  TextInput,
} from "../../components/ui";
import { pontoService } from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import { Tabela, Td, Th } from "./TabelaCompacta";
import { dataCompleta } from "./pontoUtils";

const NOVO_VAZIO = {
  usuarioId: "",
  tipo: "FERIAS",
  dataInicio: "",
  dataFim: "",
  observacao: "",
};

const quantidadeDias = (inicio, fim) =>
  Math.round(
    (new Date(`${fim}T00:00:00`) - new Date(`${inicio}T00:00:00`)) / 86400000,
  ) + 1;

/** Férias, atestados, folgas, faltas abonadas, licenças e suspensões. */
const AfastamentosTab = ({ colaboradoras }) => {
  const { showError, showSuccess } = useNotification();
  const confirmar = useConfirmDialog();
  const [ano, setAno] = useState(new Date().getFullYear());
  const [usuarioFiltro, setUsuarioFiltro] = useState("");
  const [tipos, setTipos] = useState([]);
  const [afastamentos, setAfastamentos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [novo, setNovo] = useState(NOVO_VAZIO);

  useEffect(() => {
    pontoService
      .listarTiposAfastamento()
      .then(setTipos)
      .catch((erro) => showError(erro.message));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setAfastamentos(
        await pontoService.listarAfastamentos(ano, usuarioFiltro),
      );
    } catch (erro) {
      showError(erro.message);
    } finally {
      setCarregando(false);
    }
  }, [ano, usuarioFiltro]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    carregar();
  }, [carregar]);

  const cadastrar = async (evento) => {
    evento.preventDefault();
    setSalvando(true);
    try {
      await pontoService.cadastrarAfastamento({
        ...novo,
        observacao: novo.observacao.trim() || null,
      });
      showSuccess("Afastamento cadastrado.");
      setNovo({ ...NOVO_VAZIO, usuarioId: novo.usuarioId });
      await carregar();
    } catch (erro) {
      showError(erro.message);
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (afastamento) => {
    const confirmado = await confirmar({
      title: "Excluir afastamento",
      description: `Excluir ${afastamento.tipoDescricao} de ${afastamento.nomeColaboradora}? Os dias voltam a ter jornada normal (e podem virar falta).`,
      confirmText: "Excluir",
      variant: "danger",
    });
    if (!confirmado) return;
    try {
      await pontoService.excluirAfastamento(afastamento.id);
      showSuccess("Afastamento excluído.");
      await carregar();
    } catch (erro) {
      showError(erro.message);
    }
  };

  const formularioValido =
    novo.usuarioId &&
    novo.tipo &&
    novo.dataInicio &&
    novo.dataFim &&
    novo.dataFim >= novo.dataInicio;

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
      <SectionCard bodyClassName="p-0">
        <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              icon={ChevronLeft}
              aria-label="Ano anterior"
              onClick={() => setAno(ano - 1)}
            />
            <span className="min-w-[60px] text-center font-semibold">
              {ano}
            </span>
            <Button
              variant="ghost"
              size="sm"
              icon={ChevronRight}
              aria-label="Próximo ano"
              onClick={() => setAno(ano + 1)}
            />
          </div>
          <SelectInput
            aria-label="Filtrar por colaboradora"
            className="!w-60"
            value={usuarioFiltro}
            onChange={(evento) => setUsuarioFiltro(evento.target.value)}
          >
            <option value="">Todas as colaboradoras</option>
            {colaboradoras.map((item) => (
              <option key={item.usuarioId} value={item.usuarioId}>
                {item.nome}
              </option>
            ))}
          </SelectInput>
        </div>
        {carregando ? (
          <LoadingState />
        ) : afastamentos.length === 0 ? (
          <EmptyState title={`Nenhum afastamento em ${ano}.`} />
        ) : (
          <Tabela>
            <thead>
              <tr>
                <Th>Colaboradora</Th>
                <Th>Tipo</Th>
                <Th>Período</Th>
                <Th>Observação</Th>
                <Th className="text-right">Excluir</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {afastamentos.map((afastamento) => (
                <tr key={afastamento.id}>
                  <Td>{afastamento.nomeColaboradora}</Td>
                  <Td>
                    <StatusBadge
                      variant={
                        afastamento.tipo === "SUSPENSAO" ? "danger" : "info"
                      }
                    >
                      {afastamento.tipoDescricao}
                    </StatusBadge>
                  </Td>
                  <Td className="tabular-nums">
                    {dataCompleta(afastamento.dataInicio)} a{" "}
                    {dataCompleta(afastamento.dataFim)}
                    <span className="ml-1 text-xs text-gray-500">
                      (
                      {quantidadeDias(
                        afastamento.dataInicio,
                        afastamento.dataFim,
                      )}{" "}
                      dias)
                    </span>
                  </Td>
                  <Td
                    className="max-w-[240px] truncate text-gray-500"
                    title={afastamento.observacao || ""}
                  >
                    {afastamento.observacao || ""}
                  </Td>
                  <Td className="text-right">
                    <IconButton
                      size="sm"
                      variant="danger"
                      icon={Trash2}
                      label="Excluir afastamento"
                      onClick={() => excluir(afastamento)}
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        )}
      </SectionCard>

      <SectionCard>
        <form className="space-y-4" onSubmit={cadastrar}>
          <h3 className="font-semibold text-gray-900">Novo afastamento</h3>
          <FormField
            label="Colaboradora"
            htmlFor="afastamento-usuario"
            required
          >
            <SelectInput
              id="afastamento-usuario"
              value={novo.usuarioId}
              onChange={(evento) =>
                setNovo({ ...novo, usuarioId: evento.target.value })
              }
            >
              <option value="">Selecione</option>
              {colaboradoras.map((item) => (
                <option key={item.usuarioId} value={item.usuarioId}>
                  {item.nome}
                </option>
              ))}
            </SelectInput>
          </FormField>
          <FormField label="Tipo" htmlFor="afastamento-tipo" required>
            <SelectInput
              id="afastamento-tipo"
              value={novo.tipo}
              onChange={(evento) =>
                setNovo({ ...novo, tipo: evento.target.value })
              }
            >
              {tipos.map((tipo) => (
                <option key={tipo.tipo} value={tipo.tipo}>
                  {tipo.descricao}
                </option>
              ))}
            </SelectInput>
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="De" htmlFor="afastamento-inicio" required>
              <TextInput
                id="afastamento-inicio"
                type="date"
                value={novo.dataInicio}
                onChange={(evento) =>
                  setNovo({
                    ...novo,
                    dataInicio: evento.target.value,
                    dataFim: novo.dataFim || evento.target.value,
                  })
                }
              />
            </FormField>
            <FormField label="Até" htmlFor="afastamento-fim" required>
              <TextInput
                id="afastamento-fim"
                type="date"
                min={novo.dataInicio || undefined}
                value={novo.dataFim}
                onChange={(evento) =>
                  setNovo({ ...novo, dataFim: evento.target.value })
                }
              />
            </FormField>
          </div>
          <FormField label="Observação" htmlFor="afastamento-observacao">
            <TextAreaInput
              id="afastamento-observacao"
              maxLength={500}
              placeholder="Ex.: CID não informado; atestado entregue em 05/11"
              value={novo.observacao}
              onChange={(evento) =>
                setNovo({ ...novo, observacao: evento.target.value })
              }
            />
          </FormField>
          <Button
            type="submit"
            icon={Plus}
            className="w-full"
            loading={salvando}
            disabled={!formularioValido}
          >
            Cadastrar
          </Button>
          <p className="text-xs text-gray-500">
            Dias cobertos não geram falta nem atraso. Suspensão entra no total
            “Faltas / Suspensão” do cartão.
          </p>
        </form>
      </SectionCard>
    </div>
  );
};

export default AfastamentosTab;
